import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  PLATFORM_ID,
  Inject,
  HostListener,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import { CartService } from '../../shared/services/cart.service';
import { CouponComponent } from '../coupon/coupon.component';
import { AddressComponent } from '../address/address.component';
import { GstComponent } from '../gst/gst.component';
import { LoginComponent } from '../login/login.component';
import { BehaviourSubjectService } from '../../shared/services/behaviour-subject.service';
import { PaymentService } from '../../shared/services/payment.service';
import { LoaderService } from '../../shared/services/loader.service';
import { NotificationService } from '../../shared/services/notification.service';
import { AuthService } from '../../shared/services/auth.service';
import {
  appendOnlinePaymentFields,
  assignPaymentFlow,
  extractPriceBreakup,
  getOnlinePaymentFields,
  toAmountNumber,
} from '../../shared/constants/payment-flow';
import { BuyerVerificationService } from '../../shared/services/buyer-verification.service';
import { ModalService } from '../../shared/services/modal.service';
import {
  formatAddress as formatAddressUtil,
  hasAddress as hasAddressUtil,
  shouldShowBillingBlock as shouldShowBillingBlockUtil,
} from '../../shared/utils/address.utils';
import {
  canAddGstDetails as canAddGstDetailsUtil,
  canPlaceOrder as canPlaceOrderUtil,
} from '../../shared/utils/gst-eligibility';

@Component({
  selector: 'app-buy-now-popup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CouponComponent,
    AddressComponent,
    GstComponent,
  ],
  templateUrl: './buy-now-popup.component.html',
  styleUrl: './buy-now-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuyNowPopupComponent implements OnInit, OnDestroy, OnChanges {
  @Input() productData: any;
  @Input() isOpen: boolean = false;
  @Input() quantity: number = 1; // Add quantity input
  @Input() unit: string = 'Ton'; // Add unit input
  @Output() close = new EventEmitter<void>();
  @Output() paymentSuccess = new EventEmitter<any>();

  // State management
  isBuyNowAnimating = false;
  selectedDeliveryOption: 'home' | 'store' = 'home';
  selectedBillingOption: 'Billing' | 'gstin' | null = null;
  isMobile: boolean = false;

  // Popup states
  showBillingPopup = false;
  showGSTINPopup = false;
  showAddressSelectionPopup = false;
  showCouponPopup = false;
  showLoginModal = false;

  // Address management
  addresses: any = [];
  selectedAddressId: string = '';
  addressType: 'shipping' | 'billing' = 'shipping';
  userData: any;

  get canAddGstDetails(): boolean {
    return canAddGstDetailsUtil(this.userData);
  }

  // Coupon management
  selectedCoupon: any = null;
  // Prevent cc_get_enc request from being cancelled by popup destroy
  private isPaymentInitiating = false;
  private paymentEncSub?: Subscription;

  // API call guards to prevent duplicate calls
  private userDataFetchInProgress: boolean = false;
  private addressDataFetchInProgress: boolean = false;
  private couponDataFetchInProgress: boolean = false;
  private priceBreakupFetchInProgress: boolean = false;

  // Login management
  currentAction: string = 'buy_now';

  private sub: Subscription = new Subscription();
  private destroy$ = new Subject<void>();
  ccavenue: any;
  oreder_number: any;
  // paymentService: any;
  formBuilder: any;
  isPaymentInProgress: boolean = false;
  orderDetail: any;
  availableCoupons: any;
  payFullAmount: boolean = false;
  showFullPaymentConfirm: boolean = false;
  displayPriceBreakup: any = null;
  paymentErrorMessage: string | null = null;

  get canPlaceOrder(): boolean {
    return canPlaceOrderUtil(this.userData);
  }

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private cartService: CartService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private behaviourSubject: BehaviourSubjectService,
    public paymentService: PaymentService,
    private loaderService: LoaderService,
    private cdref: ChangeDetectorRef,
    private notificationService: NotificationService,
    private modalService: ModalService,
    private authService: AuthService,
    private buyerVerificationService: BuyerVerificationService,
  ) {
    this.formBuilder = this.fb;
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth < 680;
      // Add page visibility listener for better payment gateway back detection
      this.addPageVisibilityListener();
      try {
        const userDataString = localStorage.getItem('userData1');
        if (userDataString) {
          // Check if the data looks encrypted (starts with base64 pattern like "U2FsdGVkX1")
          if (userDataString.startsWith('U2FsdGVkX1')) {
            // Data is encrypted, set empty object as fallback
            this.userData = {};
          } else {
            this.userData = JSON.parse(userDataString);
          }
        } else {
          this.userData = {};
        }
      } catch (error) {
        this.userData = {};
      }
      if (this.userData.id) {
        this.loadUserDataInParallel();
      }
      // If popup is already open on init, disable body scroll
      if (this.isOpen) {
        this.disableBodyScroll();
        // Fetch coupons data when popup opens (only on init)
        this.fetchCouponsData();
      }
    }
    // Always set selectedDeliveryOption on init, so address block is shown immediately
    if (this.productData?.part_type === 'Scrap Part') {
      this.selectedDeliveryOption = 'store';
    } else {
      this.selectedDeliveryOption = 'home';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle body scroll when popup opens/closes
    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue === true) {
        this.disableBodyScroll();
        // Reset states when popup opens
        this.resetPopupState();

        // Only fetch coupons if not already fetching and not previously fetched in this session
        if (!this.couponDataFetchInProgress && !this.availableCoupons) {
          this.fetchCouponsData();
        }

        // Refresh user + address data each time popup opens
        if (isPlatformBrowser(this.platformId)) {
          let userData = {};
          try {
            const userDataString = localStorage.getItem('userData1');
            if (userDataString && !userDataString.startsWith('U2FsdGVkX1')) {
              userData = JSON.parse(userDataString);
            }
          } catch (error) { }
          if (userData && (userData as any)?.id) {
            this.loadUserDataInParallel();
          }
        }
      } else {
        this.enableBodyScroll();
      }
    }
  }

  resetPopupState() {
    // Reset all popup states
    this.selectedBillingOption = null;
    this.showBillingPopup = false;
    this.showGSTINPopup = false;
    this.showAddressSelectionPopup = false;
    this.showCouponPopup = false;
    this.selectedAddressId = '';
    this.addressType = 'shipping';
    this.selectedCoupon = null;
    this.displayPriceBreakup = null;
    this.payFullAmount = false;
    this.showFullPaymentConfirm = false;
    this.paymentErrorMessage = null;

    if (this.productData?.part_type === 'Scrap Part') {
      this.selectedDeliveryOption = 'store';
    } else {
      this.selectedDeliveryOption = 'home';
    }
  }

  isScrapPart(): boolean {
    return (
      (this.productData?.part_type ||
        this.productData?.partType ||
        this.productData?.part_detail?.part_type) === 'Scrap Part'
    );
  }

  hasAddress(address: any): boolean {
    return hasAddressUtil(address);
  }

  shouldShowBillingBlock(): boolean {
    return shouldShowBillingBlockUtil(this.userData, {
      isScrapOrStorePickup: this.isScrapPart(),
      selectedBillingOption: this.selectedBillingOption,
    });
  }

  /**
   * Load user details and addresses in parallel for better performance
   */
  loadUserDataInParallel(): void {
    // Guard: Prevent duplicate calls
    if (this.userDataFetchInProgress) {
      return;
    }

    this.userDataFetchInProgress = true;
    this.loaderService.show();

    const userDetails$ = this.apiService.getUserDetails({}).pipe(
      catchError((err) => {
        // this.notificationService.error(
        //   err?.error?.data?.msg || 'Failed to fetch user details',
        // );
        return of(null);
      }),
    );

    const addressData$ = this.apiService
      .getAddressData({ offset: 0, limit: 10 })
      .pipe(
        catchError((err) => {
          // this.notificationService.error(
          //   err?.error?.data?.msg || 'Failed to fetch address data',
          // );
          return of(null);
        }),
      );

    this.sub.add(
      forkJoin({
        userDetails: userDetails$,
        addressData: addressData$,
      })
        .pipe(
          finalize(() => {
            this.loaderService.hide();
            this.userDataFetchInProgress = false;
          }),
        )
        .subscribe({
          next: (results) => {
            // Handle user details
            if (results.userDetails?.data) {
              localStorage.setItem(
                'userData1',
                JSON.stringify(results.userDetails.data),
              );
              this.userData = results.userDetails.data;
            }

            // Handle address data
            if (results.addressData?.success && results.addressData?.data) {
              this.addresses = results.addressData.data;
            } else {
              this.addresses = [];
            }

            this.cdref.markForCheck();
          },
          error: (err) => {
            // this.notificationService.error('Failed to load user data');
            this.cdref.markForCheck();
          },
        }),
    );
  }

  triggerLoginModal() {
    this.behaviourSubject.openLoginModal('buy_now', this.productData);
  }

  private loadUserData(): void {
    try {
      const userDataString = localStorage.getItem('userData1');
      this.userData = userDataString ? JSON.parse(userDataString) : {};
    } catch (error) {
      this.userData = {};
    }
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Note: `cc_get_enc` is not added to `this.sub`, so unsubscribing `this.sub`
    // will not cancel the payment encryption request.
    this.sub.unsubscribe();

    // Reset all guard flags
    this.userDataFetchInProgress = false;
    this.addressDataFetchInProgress = false;
    this.couponDataFetchInProgress = false;
    this.priceBreakupFetchInProgress = false;

    // Clean up event listeners
    if (isPlatformBrowser(this.platformId)) {
      // Remove page visibility listener
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
      // Ensure body scroll is enabled when component is destroyed
      this.enableBodyScroll();
    }
  }

  /**
   * Handle visibility change for cleanup
   */
  private handleVisibilityChange = () => {
    // This method is bound to this context for proper cleanup
    if (!document.hidden) {
      setTimeout(() => { }, 1000);
    }
  };

  // Buy Now Click Handler
  onBuyNowClick() {
    // Refresh user data from localStorage
    try {
      const userDataString = localStorage.getItem('userData1');
      if (userDataString && !userDataString.startsWith('U2FsdGVkX1')) {
        this.userData = JSON.parse(userDataString);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }

    // Force change detection
    this.cdref.markForCheck();

    // Double-check auth state from authService as secondary verification
    const isLoggedIn = this.authService.isLoggedIn;
    const hasValidUserData = this.userData && this.userData.id;

    if (!hasValidUserData && !isLoggedIn) {
      this.openLoginPopup('buy_now');
      return;
    }

    this.isBuyNowAnimating = true;
    setTimeout(() => {
      this.isBuyNowAnimating = false;
      this.isOpen = true;
    }, 200);
  }

  closePopup() {
    try {
      // Avoid cancelling critical payment calls (cc_get_enc) mid-flight
      if (this.isPaymentInitiating) {
        // this.notificationService.info(
        //   'Payment is being initiated. Please wait.',
        // );
        return;
      }
      // Reset states when closing popup
      this.resetPopupState();
      this.isOpen = false;
      this.enableBodyScroll(); // Ensure scroll is enabled when closing
      this.close.emit();
    } catch (error) { }
  }

  onBackdropClick(event: Event) {
    // Close only when clicking the backdrop itself (not inside the panel)
    const target = event.target as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement | null;
    if (target && currentTarget && target === currentTarget) {
      this.closePopup();
    }
  }

  selectDeliveryOption(option: 'home' | 'store') {
    this.selectedDeliveryOption = option;
  }

  onBillingOptionChange(option: 'Billing' | 'gstin') {
    this.selectedBillingOption = option;
    if (option === 'Billing') {
      this.addressType = 'billing';
      this.openAddressSelection('billing');
    } else if (option === 'gstin') {
      if (!this.canAddGstDetails) {
        this.selectedBillingOption = null;
        this.cdref.markForCheck();
        return;
      }
      this.showGSTINPopup = true;
    }
    this.cdref.markForCheck();
  }

  openAddressSelection(type: 'shipping' | 'billing') {
    this.addressType = type;

    // Get the current address ID based on type
    let currentAddressId: any = null;
    if (type === 'shipping' && this.userData?.shipping_address?.id) {
      currentAddressId = this.userData.shipping_address.id;
    } else if (type === 'billing' && this.userData?.billing_address?.id) {
      currentAddressId = this.userData.billing_address.id;
    }

    // Set selectedAddressId to current address ID so radio button is pre-selected
    this.selectedAddressId = currentAddressId;

    // Always fetch fresh address list when modal opens
    this.getAddressData();

    // Force change detection to update radio button
    this.cdref.detectChanges();

    // Open modal
    this.showAddressSelectionPopup = true;
  }

  closeAddressSelection() {
    this.showAddressSelectionPopup = false;
    this.selectedAddressId = '';
  }

  selectAddress(addressId: string) {
    this.selectedAddressId = addressId;
  }

  applyAddressSelection() {
    if (!this.selectedAddressId) {
      this.notificationService.error('Please select an address');
      return;
    }

    const payload = {
      id: this.selectedAddressId,
      shipping_address: this.addressType === 'shipping' ? true : '',
      billing_address: this.addressType === 'billing' ? true : '',
    };
    this.loaderService.show();

    this.apiService
      .updateAdress(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loaderService.hide();
          this.loadUserDataInParallel();
          this.closeAddressSelection();
          // this.notificationService.success('Address updated successfully');
          this.cdref.markForCheck();
        },
        error: (error) => {
          this.loaderService.hide();
          // this.notificationService.error(
          //   error?.error?.data?.msg || 'Failed to update address',
          // );
          this.cdref.markForCheck();
        },
      });
  }

  addNewAddress() {
    this.closeAddressSelection();
    this.showBillingPopup = true;
  }

  onCloseBillingDetails() {
    this.showBillingPopup = false;
    this.cdref.markForCheck();
  }

  onSaveAddressDetails(event: any) {
    this.showBillingPopup = false;
    this.loadUserDataInParallel();
  }

  onCloseGSTINPopup() {
    this.showGSTINPopup = false;
    this.cdref.markForCheck();
  }

  onSaveGSTINDetails(event: any) {
    this.showGSTINPopup = false;
    this.loadUserDataInParallel();
  }

  openGSTINPopup() {
    this.showGSTINPopup = true;
  }

  openCouponPopup() {
    this.showCouponPopup = true;
  }

  closeCouponPopup() {
    this.showCouponPopup = false;
  }

  onSaveCouponDetails(event: any) {
    this.selectedCoupon = event;

    // Always refresh price breakup (whether coupon added or removed)
    this.getPricebreakup();
  }

  openLoginPopup(action: string = 'buy_now'): void {
    try {
      this.currentAction = action;

      // Use global login modal through BehaviourSubjectService
      this.behaviourSubject.openLoginModal(
        action as 'buy_now' | 'other',
        this.productData,
      );
    } catch (error: any) {
      console.error('Error opening login popup:', error);
      // this.notificationService.error(
      //   error?.error?.data?.msg || 'Failed to open login. Please try again.',
      // );
    }
  }

  closeLoginPopup() {
    try {
      this.behaviourSubject.closeLoginModal();

      // Refresh auth state to ensure components get the updated login status
      setTimeout(() => {
        this.authService.refreshAuthState();

        // Reload user data after auth state refresh
        const userDataString = localStorage.getItem('userData1');
        if (userDataString && !userDataString.startsWith('U2FsdGVkX1')) {
          this.userData = JSON.parse(userDataString);

          // If user just logged in, load user details and addresses
          if (this.userData && this.userData.id) {
            this.getUserDetails();
            this.loadUserDataInParallel();
          }
        }

        this.cdref.markForCheck();
      }, 100);
    } catch (error: any) {
      console.error('Error closing login popup:', error);
      // this.notificationService.error(
      //   error?.error?.data?.msg ||
      //     'An error occurred. Please refresh the page.',
      // );
      this.cdref.markForCheck();
    }
  }

  private showPaymentError(message: string): void {
    this.paymentErrorMessage = message;
    this.notificationService.error(message);
    this.cdref.markForCheck();
  }

  async onMakePayment() {
    try {
      this.paymentErrorMessage = null;

      if (!this.userData) {
        this.showPaymentError('Please login to continue with payment');
        return;
      }

      if (
        !this.hasAddress(this.userData?.billing_address) &&
        !this.hasAddress(this.userData?.shipping_address)
      ) {
        this.showPaymentError('Please select a billing address');
        return;
      }

      const orderGate =
        await this.buyerVerificationService.ensureCanCreateOrder(this.userData);
      if (!orderGate.allowed) {
        this.showPaymentError(orderGate.message);
        return;
      }

      // For Store Pickup, skip address selection and proceed directly to payment

      const part_number =
        this.productData?.part_number ||
        this.productData?.part_detail?.part_number;
      if (!part_number) {
        this.showPaymentError(
          'Product information is missing. Please try again.',
        );
        return;
      }

      // Construct parts data
      const partsData = [
        {
          part_number: part_number,
          inventory_id: this.productData?.inventory_id,
          delivery_type:
            this.productData?.part_type === 'Scrap Part'
              ? 'Store Pickup'
              : this.selectedDeliveryOption === 'store'
                ? 'Store Pickup'
                : 'Home Delivery',
          req_quantity:
            this.productData?.part_type === 'Scrap Part'
              ? this.unit === 'Kg'
                ? this.quantity
                : this.quantity * 1000
              : 1,
        },
      ];

      // Create FormData payload
      const payload = new FormData();
      payload.append('is_buy_now', 'true');
      payload.append('coupon_code', this.selectedCoupon?.coupon_code || '');
      payload.append('offer_id', this.selectedCoupon?.row_id || '');
      payload.append('parts', JSON.stringify(partsData));
      appendOnlinePaymentFields(payload, this.getOnlinePaymentPayload());

      if (this.productData?.part_type === 'Scrap Part') {
        const quantity =
          this.unit === 'Kg' ? this.quantity : this.quantity * 1000;
        payload.append('quantity', quantity.toString());
      }

      localStorage.removeItem('createdOrderDetail');
      this.loaderService.show();

      this.apiService
        .buyNow(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (res.success && res.data) {
              this.loaderService.hide();
              this.orderDetail = res.data;
              localStorage.setItem(
                'createdOrderDetail',
                JSON.stringify(this.orderDetail),
              );
              this.oreder_number = this.orderDetail?.order_detail?.order_number;

              // this.notificationService.success('Order created successfully.');
              this.isPaymentInitiating = true;
              this.initiatePayment();
            } else {
              this.loaderService.hide();
              // this.notificationService.error(
              //   res?.data?.msg || 'Failed to create order. Please try again.',
              // );
            }
            this.cdref.markForCheck();
          },
          error: (err) => {
            this.loaderService.hide();
            this.isPaymentInitiating = false;
            // this.notificationService.error(
            //   err.error?.data?.msg ||
            //     'Failed to create order. Please try again.',
            // );
            this.cdref.markForCheck();
          },
        });
    } catch (error: any) {
      this.isPaymentInitiating = false;
      this.showPaymentError(
        error?.error?.data?.msg || 'Failed to create order. Please try again.',
      );
    }
  }

  async onMakeEnquiry() {
    try {
      this.paymentErrorMessage = null;

      if (!this.userData) {
        this.showPaymentError('Please login to continue with payment');
        return;
      }

      if (
        !this.hasAddress(this.userData?.billing_address) &&
        !this.hasAddress(this.userData?.shipping_address)
      ) {
        this.showPaymentError('Please select a billing address');
        return;
      }

      const orderGate =
        await this.buyerVerificationService.ensureCanCreateOrder(this.userData);
      if (!orderGate.allowed) {
        this.showPaymentError(orderGate.message);
        return;
      }

      const part_number =
        this.productData?.part_number ||
        this.productData?.part_detail?.part_number;
      if (!part_number) {
        this.showPaymentError(
          'Product information is missing. Please try again.',
        );
        return;
      }

      // Construct parts data
      const partsData = [
        {
          part_number: part_number,
          inventory_id: this.productData?.inventory_id,
          delivery_type:
            this.productData?.part_type === 'Scrap Part'
              ? 'Store Pickup'
              : this.selectedDeliveryOption === 'store'
                ? 'Store Pickup'
                : 'Home Delivery',
          req_quantity:
            this.productData?.part_type === 'Scrap Part'
              ? this.unit === 'Kg'
                ? this.quantity
                : this.quantity * 1000
              : 1,
        },
      ];

      // Create FormData payload for enquiry
      const payload = new FormData();
      payload.append('is_buy_now', 'true');
      payload.append('coupon_code', this.selectedCoupon?.coupon_code || '');
      payload.append('offer_id', this.selectedCoupon?.row_id || '');
      payload.append('parts', JSON.stringify(partsData));
      payload.append('is_enquiry', 'true');

      if (this.productData?.part_type === 'Scrap Part') {
        const quantity =
          this.unit === 'Kg' ? this.quantity : this.quantity * 1000;
        payload.append('quantity', quantity.toString());
      }

      localStorage.removeItem('createdOrderDetail');
      this.loaderService.show();

      this.apiService
        .buyNow(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (res.success && res.data) {
              this.loaderService.hide();
              this.orderDetail = res.data;
              localStorage.setItem(
                'createdOrderDetail',
                JSON.stringify(this.orderDetail),
              );
              this.oreder_number = this.orderDetail?.order_detail?.order_number;
              const enquiryNumber = this.orderDetail?.enquiry_numbers?.[0] || '';
              this.notificationService.success('Enquiry submitted successfully');
              this.closePopup();
              this.router.navigate(['/order-confirmation'], {
                queryParams: {
                  success: 'true',
                  order_id: this.oreder_number,
                  is_enquiry: 'true',
                  enquiry_number: enquiryNumber,
                },
                state: {
                  orderDetail: this.orderDetail,
                  isEnquiry: true,
                  enquiryNumber,
                },
              });
            } else {
              this.loaderService.hide();
              this.showPaymentError(res?.data?.msg || 'Failed to submit enquiry');
            }
            this.cdref.markForCheck();
          },
          error: (err) => {
            this.loaderService.hide();
            this.isPaymentInitiating = false;
            this.showPaymentError(
              err?.error?.data?.msg ||
              'Failed to submit enquiry. Please try again.',
            );
            this.cdref.markForCheck();
          },
        });
    } catch (error: any) {
      this.isPaymentInitiating = false;
      this.showPaymentError(
        error?.error?.data?.msg || 'Failed to submit enquiry. Please try again.',
      );
    }
  }

  getUserDetails() {
    // Use loadUserDataInParallel instead for better performance
    // It fetches both user details and addresses in one go
    this.loadUserDataInParallel();
  }

  getAddressData() {
    // Guard: Prevent duplicate calls
    if (this.addressDataFetchInProgress) {
      return;
    }

    this.addressDataFetchInProgress = true;

    try {
      const payload = {
        offset: 0,
        limit: 10,
      };
      this.loaderService.show();

      this.apiService
        .getAddressData(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            if (res && res.success && res.data) {
              this.addresses = res.data;
            } else {
              this.addresses = [];
            }
            this.loaderService.hide();
            this.addressDataFetchInProgress = false;
            this.cdref.markForCheck();
          },
          error: (err) => {
            this.addresses = [];
            this.loaderService.hide();
            this.addressDataFetchInProgress = false;
            // this.notificationService.error(
            //   err?.error?.data?.msg || 'Failed to fetch address data',
            // );
            this.cdref.markForCheck();
          },
        });
    } catch (error: any) {
      this.addresses = [];
      this.loaderService.hide();
      this.addressDataFetchInProgress = false;
      // this.notificationService.error(
      //   error?.error?.data?.msg || 'Failed to fetch address data',
      // );
    }
  }

  getActivePriceBreakup(): any {
    return this.displayPriceBreakup || this.productData?.price_breakup;
  }

  calculateTotalPrice(): number {
    return (
      this.getActivePriceBreakup()?.order_total ||
      this.getActivePriceBreakup()?.total_amount ||
      this.productData?.part_detail?.discounted_price ||
      0
    );
  }

  getFormattedPrice(price: number): string {
    if (price === undefined || price === null || isNaN(price)) {
      return '₹0';
    }
    return `₹${price.toLocaleString('en-IN')}`;
  }

  getBasePrice(): number {
    const breakup = this.getActivePriceBreakup();
    const orderTotal = Number(breakup?.order_total || breakup?.orderTotal || 0);
    if (orderTotal > 0) {
      return orderTotal;
    }
    const breakupBase = Number(breakup?.base_price || 0);
    if (breakupBase > 0) {
      return breakupBase;
    }
    const partDetailBase = Number(
      this.productData?.part_detail?.order_total ||
        this.productData?.order_total ||
        this.productData?.part_detail?.base_price ||
        0,
    );
    if (partDetailBase > 0) {
      return partDetailBase;
    }
    const partDetail = this.productData?.part_detail || this.productData;
    const qty = this.getTotalQuantity();
    if (
      this.isScrapPart() ||
      partDetail?.part_type === 'Scrap Part' ||
      this.productData?.partType === 'Scrap Part'
    ) {
      const rate = partDetail?.discounted_taxable
        ? Number(partDetail.discounted_taxable)
        : (Number(partDetail?.taxable_amount) || Number(partDetail?.price_s) || 0);
      if (rate > 0) {
        return rate * (qty || 1);
      }
    }
    const price = Number(partDetail?.discounted_price || partDetail?.price_s || 0);
    return price * (qty || 1);
  }

  getBalanceAmount(): number {
    const breakup = this.getActivePriceBreakup();
    const remaining = Number(
      breakup?.remaining_booking_amount ||
        breakup?.remainingBookingAmount ||
        0,
    );
    if (remaining > 0) {
      return remaining;
    }
    const base = this.getBasePrice();
    const booking = toAmountNumber(this.getBookingAmount());
    return Math.max(base - booking, 0);
  }

  getBookingAmount(): number {
    return (
      this.getActivePriceBreakup()?.scrap_booking_amount ||
      this.productData?.part_detail?.scrap_booking_amount ||
      this.productData?.part_detail?.booking_amount ||
      this.productData?.booking_amount ||
      0
    );
  }

  getBookingPercentage(): string {
    const breakup = this.getActivePriceBreakup();
    if (breakup?.booking_percent_rule) {
      return breakup.booking_percent_rule.toString();
    }
    if (this.productData?.booking_percent_rule) {
      return this.productData.booking_percent_rule.toString();
    }
    if (this.productData?.part_detail?.booking_percent_rule) {
      return this.productData.part_detail.booking_percent_rule.toString();
    }
    const base = this.getBasePrice();
    const booking = toAmountNumber(this.getBookingAmount());
    if (booking > 0 && base > 0) {
      const pct = Math.round((booking / base) * 100);
      if (pct > 0 && pct <= 100) return pct.toString();
    }
    return '';
  }

  hasBookingAmount(): boolean {
    return toAmountNumber(this.getBookingAmount()) > 0;
  }

  getOnlinePaymentPayload() {
    return getOnlinePaymentFields({
      payFullAmount: this.payFullAmount,
      bookingAmount: this.getBookingAmount(),
      totalAmount: this.calculateTotalPrice(),
    });
  }

  getDisplayTotalAmount(): any {
    const breakupTotal = toAmountNumber(
      this.getActivePriceBreakup()?.total_amount ||
        this.getActivePriceBreakup()?.totalAmount,
    );
    if (breakupTotal > 0) {
      return breakupTotal;
    }
    const totalAmount = toAmountNumber(
      this.productData?.part_detail?.total_amount ||
        this.productData?.total_amount,
    );
    if (totalAmount > 0) {
      return totalAmount;
    }
    return this.getOnlinePaymentPayload().online_amount;
  }

  onPayFullAmountChange(checked: boolean): void {
    this.payFullAmount = !!checked;
    this.showFullPaymentConfirm = false;
    this.cdref.detectChanges();
    this.getPricebreakup();
  }

  confirmFullPayment(): void {
    this.payFullAmount = true;
    this.showFullPaymentConfirm = false;
    this.cdref.detectChanges();
    this.getPricebreakup();
  }

  cancelFullPayment(): void {
    this.payFullAmount = false;
    this.showFullPaymentConfirm = false;
    this.cdref.detectChanges();
  }

  getDiscountAmount(): number {
    return (
      this.getActivePriceBreakup()?.discount ||
      this.productData?.part_detail?.discount_amount ||
      0
    );
  }

  getGSTAmount(): number {
    // const basePrice = this.getBasePrice();
    // const discount = this.getDiscountAmount();
    // const platformCharges = 100;
    // const deliveryCharges = this.selectedDeliveryOption === 'home' ? 100 : 0;
    // return (basePrice - discount + platformCharges + deliveryCharges) * 0.18;
    return (
      this.getActivePriceBreakup()?.gst_amount ||
      this.productData?.part_detail?.gst_amount ||
      0
    );
  }

  getTCSAmount(): number {
    return (
      this.getActivePriceBreakup()?.tcs_amount ||
      this.productData?.part_detail?.tcs_amount ||
      0
    );
  }

  getOfferDiscount(): number {
    return (
      this.getActivePriceBreakup()?.offer_discount ||
      this.productData?.part_detail?.offer_discount ||
      0
    );
  }

  getPlatformCharges(): number {
    return (
      this.getActivePriceBreakup()?.platform_charges ||
      this.productData?.part_detail?.platform_charges ||
      0
    );
  }

  getDeliveryCharges(): number {
    return (
      this.productData?.delivery_charges ||
      this.productData?.part_detail?.delivery_charges ||
      0
    );
  }

  // Helper methods to handle different product data structures
  getProductImage(): string {
    // Handle both product list structure and product detail structure
    return (
      this.productData?.part_image_path[0] ||
      this.productData?.part_image_path ||
      this.productData?.part_detail?.part_image_path ||
      this.productData?.part_detail?.part_image_path[0] ||
      ''
    );
  }

  getProductDescription(): string {
    // Handle both product list structure and product detail structure
    return (
      this.productData?.short_desc_s ||
      this.productData?.part_detail?.short_desc_s ||
      ''
    );
  }

  getProductDetails(): string {
    // Handle both product list structure and product detail structure
    if (
      this.productData?.mapping_data &&
      this.productData.mapping_data.length > 0
    ) {
      const mapping = this.productData.mapping_data[0];
      return `${mapping.make || ''}, ${mapping.model || ''}, ${this.productData?.part_detail?.year_of_manufacture || ''
        }, ${mapping.GROUP_NAME_s || ''}`;
    } else {
      // For product list structure, return basic info
      return `${this.productData?.branch_code_s || ''}`;
      // , ${
      //   this.productData?.quality_s || ''
      // }`;
    }
  }

  getProductPrice(): string {
    // Handle both product list structure and product detail structure
    const partDetail = this.productData?.part_detail || this.productData;
    const price =
      partDetail?.price_s ||
      partDetail?.taxable_amount ||
      this.productData?.price_s ||
      this.productData?.taxable_amount ||
      '';
    return price ? String(price) : '';
  }

  getDiscountedProductPrice(): string {
    // Handle both product list structure and product detail structure
    const partDetail = this.productData?.part_detail || this.productData;
    const price =
      partDetail?.discounted_taxable ||
      this.productData?.discounted_taxable ||
      partDetail?.discounted_price ||
      this.productData?.discounted_price ||
      partDetail?.taxable_amount ||
      this.productData?.taxable_amount ||
      '';
    return price ? String(price) : '';
  }

  getOriginalBasePrice(): number {
    const partDetail = this.productData?.part_detail || this.productData;
    const qty = this.getTotalQuantity();
    if (this.isScrapPart()) {
      const originalRate = Number(
        partDetail?.taxable_amount ||
          this.productData?.taxable_amount ||
          partDetail?.price_s ||
          this.productData?.price_s ||
          0,
      );
      if (originalRate > 0) {
        return originalRate * (qty || 1);
      }
    }
    const originalPrice = Number(
      partDetail?.price_s ||
        this.productData?.price_s ||
        partDetail?.taxable_amount ||
        this.productData?.taxable_amount ||
        0,
    );
    return originalPrice * (qty || 1);
  }

  hasDiscount(): boolean {
    const partDetail = this.productData?.part_detail || this.productData;
    const discountPct = Number(
      this.productData?.discount_percentage ||
        partDetail?.discount_percentage ||
        0,
    );
    if (discountPct > 0) {
      return true;
    }
    return (
      this.getOriginalBasePrice() > this.getBasePrice() &&
      this.getBasePrice() > 0
    );
  }

  getDiscountPercentage(): number {
    const partDetail = this.productData?.part_detail || this.productData;
    const pct = Number(
      this.productData?.discount_percentage ||
        partDetail?.discount_percentage ||
        0,
    );
    if (pct > 0) {
      return pct;
    }
    const base = this.getBasePrice();
    const original = this.getOriginalBasePrice();
    if (original > base && original > 0) {
      return Math.round(((original - base) / original) * 100);
    }
    return 0;
  }

  getQuantityUnit(): string {
    const selectedUnit = (
      this.productData?.selectedUnit ||
      this.unit ||
      ''
    ).toLowerCase();
    if (selectedUnit === 'liter' || selectedUnit === 'litr') {
      return 'Ltr';
    }
    return 'Kg';
  }

  getSellerName(): string {
    // Handle both product list structure and product detail structure
    return (
      this.productData?.branch_code_s ||
      this.productData?.seller_detail?.branch_name ||
      ''
    );
  }

  get billingName(): string {
    // First check if user has selected a specific billing option
    if (
      this.selectedBillingOption === 'Billing' &&
      this.userData?.billing_address?.name
    ) {
      return this.userData.billing_address.name;
    }

    // If no billing option is selected but user has a different billing address, show billing name
    if (
      !this.selectedBillingOption &&
      this.userData?.billing_address &&
      this.userData?.billing_address?.id !==
      this.userData?.shipping_address?.id &&
      this.userData?.billing_address?.name
    ) {
      return this.userData.billing_address.name;
    }

    // For GSTIN option or when billing and shipping are same, show shipping name
    return this.userData?.shipping_address?.name;
  }

  getBillingDisplayName(): string {
    // First check if user has selected a specific billing option
    if (
      this.selectedBillingOption === 'Billing' &&
      this.userData?.billing_address
    ) {
      return (
        this.userData.billing_address.receiver_name ||
        this.userData.billing_address.company_name ||
        this.userData.first_name + ' ' + this.userData.last_name
      );
    }

    // If no billing option is selected but user has a different billing address, show billing details
    if (
      !this.selectedBillingOption &&
      this.userData?.billing_address &&
      this.userData?.billing_address?.id !== this.userData?.shipping_address?.id
    ) {
      return (
        this.userData.billing_address.receiver_name ||
        this.userData.billing_address.company_name ||
        this.userData.first_name + ' ' + this.userData.last_name
      );
    }

    // For GSTIN option or when billing and shipping are same, show shipping details
    return (
      this.userData?.shipping_address?.receiver_name ||
      this.userData?.shipping_address?.company_name ||
      this.userData?.first_name + ' ' + this.userData?.last_name
    );
  }

  getBillingAddressType(): string {
    // First check if user has selected a specific billing option
    if (
      this.selectedBillingOption === 'Billing' &&
      this.userData?.billing_address
    ) {
      return this.userData.billing_address.address_type || '';
    }

    // If no billing option is selected but user has a different billing address, show billing address type
    if (
      !this.selectedBillingOption &&
      this.userData?.billing_address &&
      this.userData?.billing_address?.id !== this.userData?.shipping_address?.id
    ) {
      return this.userData.billing_address.address_type || '';
    }

    // For GSTIN option or when billing and shipping are same, show shipping address type
    return this.userData?.shipping_address?.address_type || '';
  }

  get billingAddress(): string {
    // First check if user has selected a specific billing option
    if (
      this.selectedBillingOption === 'Billing' &&
      this.userData?.billing_address
    ) {
      return this.formatAddress(this.userData.billing_address);
    }

    // If no billing option is selected but user has a different billing address, show it
    if (
      !this.selectedBillingOption &&
      this.userData?.billing_address &&
      this.userData?.billing_address?.id !== this.userData?.shipping_address?.id
    ) {
      return this.formatAddress(this.userData.billing_address);
    }

    // For GSTIN option or when billing and shipping are same, show shipping address
    return this.formatAddress(this.userData?.shipping_address);
  }

  formatAddress(address: any): string {
    return formatAddressUtil(address);
  }

  get billingGST(): string {
    if (this.userData?.billing_address?.gstin_number) {
      return this.userData?.billing_address?.gstin_number;
    }
    return 'No GSTIN';
  }

  onBillingChangeClick() {
    if (this.selectedBillingOption === 'Billing') {
      this.openAddressSelection('billing');
    } else if (this.selectedBillingOption === 'gstin') {
      this.openGSTINPopup();
    } else {
      // Default: open address selection for billing
      this.openAddressSelection('billing');
    }
  }

  initiatePayment() {
    if (!this.oreder_number) {
      this.isPaymentInitiating = false;
      // this.notificationService.error(
      //   'Order number not available. Please try again.',
      // );
      return;
    }

    // Create payment request payload
    const paymentPayload = {
      order_number: this.oreder_number,
      payment_flow: this.getOnlinePaymentPayload().payment_flow,
    };

    this.loaderService.show();
    // Call payment service to get encrypted payment data
    // Do NOT add this subscription to `this.sub`.
    // The popup can be destroyed via parent `*ngIf`, which would cancel this request.
    this.paymentEncSub = this.paymentService
      .getEncryptedPaymentData(paymentPayload)
      .subscribe({
        next: (response: any) => {
          if (response.success && response.data) {
            this.loaderService.hide();
            this.ccavenue = response?.data;
            let encRequest = response?.data;
            let ccavenuForm: FormGroup = this.formBuilder.group({
              encRequest: [encRequest],
              access_code: [this.ccavenue?.access_code],
            });
            this.payWithCcavenu(ccavenuForm);

            // Update payment status
            // this.paymentService.updatePaymentStatus({ status: 'in_progress' });
            // this.isPaymentInProgress = true;

            // Store payment data
            // this.paymentService.updatePaymentData(response.data);

            // Handle successful payment data
            // this.handlePaymentData(response.data);
          } else {
            this.loaderService.hide();
            this.isPaymentInitiating = false;

            // this.notificationService.error(response?.error?.data?.msg); // || 'Payment failed. Please try again.');
          }
        },
        error: (error: any) => {
          this.loaderService.hide();
          this.isPaymentInitiating = false;

          //  this.notificationService.error(error?.error?.data?.msg);
        },
        complete: () => {
          // Once request completes, allow popup to be closed again if still visible
          // (typically we navigate to payment gateway on success)
        },
      });
  }

  payWithCcavenu(formGroup: FormGroup) {
    // localStorage.removeItem('productOfferData');

    if (!this.ccavenue || !this.ccavenue.ccavenue_url) {
      this.notificationService.error('Payment gateway configuration error');
      return;
    }

    const myform = document.createElement('form');
    myform.method = 'POST';

    // Fix the action URL construction
    const baseUrl = this.ccavenue.ccavenue_url;
    const separator = baseUrl.includes('?') ? '&' : '?';
    myform.action = `${baseUrl}${separator}encRequest=${this.ccavenue.encRequest}&access_code=${this.ccavenue.access_code}`;

    let input: any;
    for (const [key, value] of Object.entries(formGroup.value)) {
      input = document.createElement('input');
      input.name = key;
      input.id = key;
      input.value = value;
      myform.appendChild(input);
    }

    myform.style.display = 'none';

    // Remove invalid append calls - these are not valid for form elements
    // myform.append("Content-Type", "application/x-www-form-urlencoded");
    // myform.append("Accept", "text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8");

    document.body.appendChild(myform);
    myform.submit();
  }

  /**
   * Handle browser back button press
   * This will detect when user navigates back from payment gateway
   */
  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) { }

  /**
   * Add page visibility listener for better payment gateway back detection
   */
  private addPageVisibilityListener() {
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
    }
  }

  /**
   * Disable body scroll when popup is open
   */
  private disableBodyScroll(): void {
    this.modalService.openModal('buy-now-popup', () => this.closePopup());
  }

  /**
   * Enable body scroll when popup is closed
   */
  private enableBodyScroll(): void {
    this.modalService.closeModal('buy-now-popup');
  }

  /** Resolves order/coupon quantity from @Input() or productData (scrap custom qty). */
  getTotalQuantity(): number {
    const partType =
      this.productData?.part_type || this.productData?.partType || '';

    if (partType !== 'Scrap Part') {
      return this.quantity || 1;
    }

    const selectedUnit = (
      this.productData?.selectedUnit ||
      this.unit ||
      ''
    ).toLowerCase();

    if (selectedUnit === 'kg') {
      if (this.productData?.customKgQuantity) {
        return parseFloat(this.productData.customKgQuantity) || this.quantity || 1;
      }
      return this.quantity || 1;
    }

    if (selectedUnit === 'liter' || selectedUnit === 'litr') {
      if (this.productData?.customLiterQuantity) {
        return parseFloat(this.productData.customLiterQuantity) || this.quantity || 1;
      }
      return this.quantity || 1;
    }

    // Ton (stored/sent as kg for APIs)
    if (this.productData?.quantity === 'custom' && this.productData?.customTonQuantity) {
      return (parseFloat(this.productData.customTonQuantity) || 0) * 1000;
    }

    if (
      typeof this.productData?.quantity === 'number' &&
      this.productData.quantity > 0
    ) {
      return this.productData.quantity * 1000;
    }

    return (this.quantity || 1) * 1000;
  }

  fetchCouponsData() {
    // Guard: Prevent duplicate calls
    if (this.couponDataFetchInProgress) {
      return;
    }

    this.couponDataFetchInProgress = true;

    // Use server cart data if available, otherwise use local cart items
    // Map cart items to payload format
    let allItems: any[] = [
      {
        negotiated_price:
          this.productData?.negotiated_price ||
          this.productData?.pricePerUnit ||
          0,
        discounted_taxable: this.productData?.discounted_taxable || 0,
        total_quantity: this.getTotalQuantity(),
        part_type:
          this.productData?.partType || this.productData?.part_type || '',
        part_number: this.productData?.part_number || '',
        scrap_category:
          this.productData?.scrap_category || this.productData?.category || '',
        seller_code: this.productData?.seller_code_s || '',
        make: this.productData?.make || '',
        model: this.productData?.model || '',
        tax_s: this.productData?.tax_s || 0,
        final_total: this.productData?.final_total || 0,
      },
    ];

    const payload = {
      items: allItems,
      //  "user_id": this.getUserId(),
    };

    // this.loadingCoupons = true;
    this.apiService
      .getBuyerBestOffer(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && response.success && response.data) {
            // Transform the data to match coupon component expectations
            this.availableCoupons = response.data.map((coupon: any) => ({
              ...coupon,
              savings: coupon.discount_amount || 0,
              isActive: true, // Set all fetched coupons as active by default
            }));
          } else {
            this.availableCoupons = [];
          }
          this.couponDataFetchInProgress = false;
          this.cdref.markForCheck();
        },
        error: (error: any) => {
          this.availableCoupons = [];
          this.couponDataFetchInProgress = false;
          // this.notificationService.error(
          //   error?.error?.data?.msg ||
          //     'Failed to load coupons. Please try again.',
          // );
          this.cdref.markForCheck();
        },
        complete: () => {
          // this.loadingCoupons = false;
        },
      });
  }

  getPricebreakup() {
    this.priceBreakupFetchInProgress = true;

    const totalQuantity = this.getTotalQuantity();

    const payload: Record<string, unknown> = {
      coupon_code: this.selectedCoupon?.coupon_code || '',
      offer_id: this.selectedCoupon?.row_id || '',
      parts: [
        {
          part_number: this.productData?.part_number,
          part_type: this.productData?.part_type,
          total_quantity: totalQuantity,
        },
      ],
    };
    assignPaymentFlow(payload, this.getOnlinePaymentPayload().payment_flow);

    this.loaderService.show();

    this.apiService
      .getBuyNowPriceBreakup(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const breakup = extractPriceBreakup(res);
          if (res.success && breakup) {
            this.displayPriceBreakup = { ...breakup };
            this.productData = {
              ...this.productData,
              price_breakup: this.displayPriceBreakup,
            };
            this.showCouponPopup = false;
            this.cdref.detectChanges();
          } else {
            // this.notificationService.error(
            //   res?.data?.msg || 'Failed to update price',
            // );
          }
          this.loaderService.hide();
          this.priceBreakupFetchInProgress = false;
        },
        error: (err) => {
          this.loaderService.hide();
          this.priceBreakupFetchInProgress = false;
          // this.notificationService.error(
          //   err?.error?.data?.msg || 'Failed to get price breakup',
          // );
          this.cdref.detectChanges();
        },
      });
  }
}
